import { API_BASE_URL } from '../config/api';

/**
 * Format location data for display
 * Handles both string and object formats
 * @param {string|object} location - Location data
 * @returns {string} Formatted location string
 */
export const formatLocation = (location) => {
  if (!location) return 'Location not specified';
  
  if (typeof location === 'string') {
    return location;
  }
  
  if (typeof location === 'object') {
    // Handle various location object formats
    const parts = [];
    
    // Check for city
    if (location.city) parts.push(location.city);
    if (location.location_city) parts.push(location.location_city);
    
    // Check for state
    if (location.state) parts.push(location.state);
    if (location.location_state) parts.push(location.location_state);
    
    // Check for address
    if (location.address) parts.push(location.address);
    if (location.street) parts.push(location.street);
    
    // Check for country
    if (location.country && location.country !== 'Pakistan') {
      parts.push(location.country);
    }
    
    const formatted = parts.filter(Boolean).join(', ');
    return formatted || 'Location not specified';
  }
  
  return 'Location not specified';
};

 // Format prices
 export const formatPrice = (price) => {
  // If already formatted (contains $ or PKR), return as is
  if (typeof price === 'string' && (price.includes('$') || price.includes('PKR') || price.includes('Rs') || price.includes('USD'))) {
    return price;
  }
  
  // If it's a string but not formatted, try to parse it
  if (typeof price === 'string') {
    // Remove any commas or spaces
    const cleaned = price.replace(/[,\s]/g, '');
    const num = parseFloat(cleaned);
    if (isNaN(num)) {
      return 'N/A';
    }
    return formatNumericPrice(num);
  }
  
  // If it's a number, format it
  if (typeof price === 'number') {
    return formatNumericPrice(price);
  }
  
  return 'N/A';
};

// Helper function to format numeric prices
const formatNumericPrice = (num) => {
  if (isNaN(num)) return 'N/A';
  
  // Format in USD (US Dollars)
  if (num >= 1000000000) {
    return `$${(num / 1000000000).toFixed(1)}B`;
  } else if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `$${(num / 1000).toFixed(0)}K`;
  }
  return `$${num.toLocaleString()}`;
};


/**
 * Format currency for display
 * Handles both string and number formats
 * @param {string|number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (typeof amount === 'string') {
    return amount; // Already formatted by backend
  }
  
  if (typeof amount === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  }
  
  return '$0';
};

/**
 * Format percentage for display
 * Handles both string and number formats
 * @param {string|number} percentage - Percentage to format
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage) => {
  if (typeof percentage === 'string') {
    return percentage;
  }
  
  if (typeof percentage === 'number') {
    return `${percentage.toFixed(2)}%`;
  }
  
  return '0.00%';
};

/**
 * Get the first available image from property data
 * Handles various image formats from different API responses
 * Uses images from database as-is (full URLs or relative paths)
 * @param {object} property - Property object
 * @returns {string|null} Image URL or null
 */
export const getPropertyImage = (property) => {
  if (!property) return null;
  
  // Helper to ensure full URL
  const ensureFullUrl = (url) => {
    if (!url) return null;
    if (typeof url !== 'string') return null;
    // If already a full URL (starts with http:// or https://), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If starts with /, it's a relative path - prepend API base URL
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    // Otherwise, assume it's a relative path and prepend API base URL with /
    return `${API_BASE_URL}/${url}`;
  };

  // Parse images - can be JSON string, array, or object
  let images = property.images;
  
  // Parse JSON string
  if (typeof images === 'string') {
    const trimmed = images.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        images = JSON.parse(images);
      } catch (e) {
        console.warn('❌ getPropertyImage - Failed to parse JSON:', e);
        images = null;
      }
    }
  }
   
  // Direct image property (from database)
  if (property.image) {
    return ensureFullUrl(property.image);
  }
  
  // Handle images object or array
  if (images) {
    // Images object with urls array: { urls: ['url1', 'url2', ...] }
    if (images.urls && Array.isArray(images.urls) && images.urls.length > 0) {
      return ensureFullUrl(images.urls[0]);
    }
    
    // Images object with thumbnail
    if (images.thumbnail) {
      return ensureFullUrl(images.thumbnail);
    }
    
    // Images object with gallery array
    if (images.gallery && Array.isArray(images.gallery) && images.gallery.length > 0) {
      return ensureFullUrl(images.gallery[0]);
    }
    
    // Direct images array
    if (Array.isArray(images) && images.length > 0) {
      const firstImg = images[0];
      if (typeof firstImg === 'string') {
        return ensureFullUrl(firstImg);
      }
      if (typeof firstImg === 'object' && firstImg.url) {
        return ensureFullUrl(firstImg.url);
      }
    }
    
    // Images object with other keys (like main, primary, etc.)
    if (typeof images === 'object' && !Array.isArray(images)) {
      const values = Object.values(images);
      for (const val of values) {
        if (typeof val === 'string' && val.length > 0) {
          return ensureFullUrl(val);
        }
        if (typeof val === 'object' && val.url) {
          return ensureFullUrl(val.url);
        }
      }
    }
  }
  
  // Check for image_url field
  if (property.image_url) {
    return ensureFullUrl(property.image_url);
  }
  
  // Check for primary_image field
  if (property.primary_image) {
    return ensureFullUrl(property.primary_image);
  }
  
  return null;
};

/**
 * Get all available images from property data
 * Uses images from database as-is (full URLs or relative paths)
 * @param {object} property - Property object
 * @returns {array} Array of image URLs
 */
export const getPropertyImages = (property) => {
  if (!property) return [];
  
  // Helper to ensure full URL
  const ensureFullUrl = (url) => {
    if (!url) return null;
    if (typeof url !== 'string') return null;
    // If already a full URL (starts with http:// or https://), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If starts with /, it's a relative path - prepend API base URL
    if (url.startsWith('/')) {
      return `${API_BASE_URL}${url}`;
    }
    // Otherwise, assume it's a relative path and prepend API base URL with /
    return `${API_BASE_URL}/${url}`;
  };

  // Parse images - can be JSON string, array, or object
  let images = property.images;
  
  // Parse JSON string
  if (typeof images === 'string') {
    const trimmed = images.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        images = JSON.parse(images);
      } catch (e) {
        console.warn('❌ getPropertyImages - Failed to parse JSON:', e);
        images = null;
      }
    }
  }
  
  // Handle images object or array
  if (images) {
    // Images object with urls array: { urls: ['url1', 'url2', ...] }
    if (images.urls && Array.isArray(images.urls)) {
      return images.urls
        .map(url => ensureFullUrl(url))
        .filter(Boolean);
    }
    
    // Images object with gallery array
    if (images.gallery && Array.isArray(images.gallery)) {
      return images.gallery
        .map(img => typeof img === 'string' ? ensureFullUrl(img) : (img?.url ? ensureFullUrl(img.url) : null))
        .filter(Boolean);
    }
    
    // Direct images array
    if (Array.isArray(images)) {
      return images
        .map(img => {
          if (typeof img === 'string') return ensureFullUrl(img);
          if (typeof img === 'object' && img.url) return ensureFullUrl(img.url);
          return null;
        })
        .filter(Boolean);
    }
    
    // Images object with other keys - collect all URL values
    if (typeof images === 'object' && !Array.isArray(images)) {
      const result = [];
      for (const val of Object.values(images)) {
        if (typeof val === 'string' && val.length > 0) {
          const url = ensureFullUrl(val);
          if (url) result.push(url);
        } else if (typeof val === 'object' && val.url) {
          const url = ensureFullUrl(val.url);
          if (url) result.push(url);
        }
      }
      if (result.length > 0) return result;
    }
  }
  
  // Single image fallback
  const singleImage = getPropertyImage(property);
  return singleImage ? [singleImage] : [];
};

/**
 * Get all documents from property data
 * Handles JSON string parsing from database (similar to images)
 * @param {object} property - Property object
 * @returns {array} Array of document objects with name, url, and type
 */
export const getPropertyDocuments = (property) => {
  if (!property) {
    console.log('⚠️ getPropertyDocuments - No property provided');
    return { brochure: null, floorPlan: null, compliance: [] };
  }
  
  // Try multiple possible field names (documents, Documents, document, etc.)
  let documents = property.documents;
  console.log('🔍 getPropertyDocuments - Input:', {
    hasProperty: !!property,
    hasDocuments: !!documents,
    documentsType: typeof documents,
    documentsValue: documents,
    isArray: Array.isArray(documents),
    isObject: typeof documents === 'object' && !Array.isArray(documents)
  });
  
  // If documents is null or undefined, return empty structure
  if (!documents) {
    console.log('⚠️ getPropertyDocuments - No documents field in property');
    return { brochure: null, floorPlan: null, compliance: [] };
  }
  
  // Parse documents if it's a JSON string (from database)
  if (typeof documents === 'string') {
    try {
      documents = JSON.parse(documents);
      console.log('✅ getPropertyDocuments - Parsed JSON:', documents);
    } catch (e) {
      console.warn('❌ getPropertyDocuments - Failed to parse JSON:', e, 'Raw:', documents);
      return { brochure: null, floorPlan: null, compliance: [] };
    }
  }
  
  // If documents is already the new object structure, use it directly
  if (typeof documents === 'object' && !Array.isArray(documents)) {
    return {
      brochure: documents.brochure || null,
      floorPlan: documents.floorPlan || null,
      compliance: Array.isArray(documents.compliance) ? documents.compliance : []
    };
  }
  
  // Legacy: If documents is an array (old format), convert to new structure
  if (Array.isArray(documents)) {
    console.log('⚠️ getPropertyDocuments - Converting legacy array format to new structure');
    const brochure = documents.find(d => d.type === 'brochure' || d.name?.toLowerCase().includes('brochure'));
    const floorPlan = documents.find(d => d.type === 'floor_plan' || d.type === 'floor-plan' || d.name?.toLowerCase().includes('floor'));
    const compliance = documents.filter(d => 
      d.type === 'compliance' || 
      d.type === 'legal' || 
      d.type === 'noc' ||
      (d.name?.toLowerCase().includes('permit') || d.name?.toLowerCase().includes('clearance'))
    );
    
    return {
      brochure: brochure ? {
        url: brochure.url,
        name: brochure.name || 'Brochure',
        notes: brochure.notes || '',
        uploadedAt: brochure.uploadedAt || new Date().toISOString(),
        uploadedBy: brochure.uploadedBy || 'admin'
      } : null,
      floorPlan: floorPlan ? {
        url: floorPlan.url,
        version: floorPlan.version || 'A',
        mimeType: floorPlan.mimeType || 'application/pdf'
      } : null,
      compliance: compliance.map(c => ({
        url: c.url,
        type: c.type === 'compliance' ? c.name : c.type,
        issuedAt: c.issuedAt || '',
        issuedBy: c.issuedBy || ''
      }))
    };
  }
  
  console.log('⚠️ getPropertyDocuments - No valid documents found');
  return { brochure: null, floorPlan: null, compliance: [] };
};